import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "../../Components/Grid/Grid";
import ProblemModal from "../../Components/Modal/Problem/ProblemModal";
import Button from "../../Components/button/button";
import "../../Style/ProblemView.css";
import config from "../../config";
import getColumnDefs from "../../utils/Problem/problemColumnDefs";
import { ProblemData } from "../../utils/Problem/problemTypes";
import { formatDuration } from "../../utils/durationUtils";
import { authFetch } from "../../utils/Security/authFetch";
import { GridApi } from "ag-grid-community";
import localforage from "localforage";

function ProblemView() {
  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [rowData, setRowData] = useState<ProblemData[]>([]);
  const [filteredData, setFilteredData] = useState<ProblemData[]>([]);
  const [selectedproblem, setSelectedproblem] = useState<ProblemData | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const INCIDENT_CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;
  const username = localStorage.getItem("username") || "System";
  const userTeam = localStorage.getItem("position");
  const [loading, setLoading] = useState(true);
  const CACHE_KEY = `problems_${isAdmin ? "admin" : userId}`;
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
  const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
  const isForNilesat = localStorage.getItem("isForNilesat") === "true";

  const handleEdit = (data: ProblemData) => {
    navigate(`/problem-edit/${data.ticketId}`, { state: { problem: data } });
  };
  const loadMyTodo = async () => {
    if (!username) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/problems/my-todo/${username}`
      );
      const data: ProblemData[] = await res.json();

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load My Todo", e);
    } finally {
      setLoading(false);
    }
  };
  // ✅ RESET TO FULL LIST (from cache or API)
  const handleReset = async () => {
    setLoading(true);
    setSearchQuery("");

    try {
      // 1️⃣ Try restoring from cache first
      const cached = await localforage.getItem<{
        rows: ProblemData[];
        ts: number;
      }>(CACHE_KEY);

      if (cached?.rows?.length) {
        setRowData(cached.rows);
        setFilteredData(cached.rows);
      } else {
        // 2️⃣ If cache is empty, force reload from API
        window.location.reload();
      }
    } catch (e) {
      console.error("Reset failed, reloading page", e);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  // ✅ MY GROUP TODO
  const loadMyGroupTodo = async () => {
    if (!userTeam) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/problems/my-group-todo/${userTeam}`
      );
      const data: ProblemData[] = await res.json();

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load Group Todo", e);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (ticketId: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this problem?"
    );
    if (!confirmDelete) return;

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/problems/${ticketId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        const updatedData = rowData.filter(
          (problem) => problem.ticketId !== ticketId
        );
        setRowData(updatedData);
        setFilteredData(updatedData.filter(applySearchFilter));
      } else {
        console.error("Failed to delete problem");
      }
    } catch (error) {
      console.error("Error deleting problem:", error);
    }
  };

  useEffect(() => {
    localforage.removeItem(INCIDENT_CACHE_KEY);
    const fetchproblems = async () => {
      try {
        const url = isAdmin
          ? `${config.apiBaseUrl}/api/problems`
          : `${config.apiBaseUrl}/api/problems/user/${userId}`;

        const response = await authFetch(url);
        if (response.ok) {
          const data = await response.json();
          const formatted = data.map(
            (item: { problemResolutionTime: string | null | undefined }) => ({
              ...item,

              problemResolutionTime: formatDuration(item.problemResolutionTime),
            })
          );
      if (isAdmin && (isForTelebras || isForPetrobras || isForNilesat)) {
      setRowData([]);
      setFilteredData([]);
      return;
    }
          setRowData(formatted);
          setFilteredData(formatted);
        } else {
          console.error("Failed to fetch problems");
        }
      } catch (error) {
        console.error("Error fetching problems:", error);
      }
    };

    fetchproblems();
  }, [isAdmin]);

  const columnDefs = getColumnDefs(handleEdit, handleDelete);

  const handleRowClick = (data: ProblemData) => {
    setSelectedproblem(data);
    setIsModalOpen(true);
  };
  const gridApiRef = useRef<GridApi | null>(null);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedproblem(null);
  };

  const stripHtml = (html: string): string => {
    const div = document.createElement("div");

    const htmlWithImagePlaceholder = html.replace(/<img[^>]*>/gi, "[Image]");

    div.innerHTML = htmlWithImagePlaceholder;
    return div.textContent || div.innerText || "";
  };
  const handleExportCSV = () => {
    if (!gridApiRef.current) {
      alert("Grid is not ready.");
      return;
    }

    const filteredNodes: ProblemData[] = [];
    gridApiRef.current.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredNodes.push(node.data);
      }
    });

    if (!filteredNodes.length) {
      alert("No data to export.");
      return;
    }

    const exportableColumns = columnDefs.filter((col) => !!col.field);
    const headers = exportableColumns.map((col) => col.headerName || col.field);
    const fields = exportableColumns.map(
      (col) => col.field as keyof ProblemData
    );

    const sanitizeField = (value: any, field: string): string => {
      if (typeof value !== "string") return String(value ?? "");
      if (["summary", "description", "solution"].includes(field)) {
        return stripHtml(value);
      }
      if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        return value.replace("T", " ");
      }
      return value;
    };

    const csvRows = [
      headers.join(","),
      ...filteredNodes.map((row) =>
        fields
          .map((field) => JSON.stringify(sanitizeField(row[field], field)))
          .join(",")
      ),
    ];

    const blob = new Blob(["﻿" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "problem.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const applySearchFilter = (item: ProblemData): boolean => {
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(query)
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFilteredData(
      rowData.filter((item) =>
        Object.values(item).some(
          (val) =>
            val !== null &&
            val !== undefined &&
            val.toString().toLowerCase().includes(value.toLowerCase())
        )
      )
    );
  };

  return (
    <>
      <div className="top">
        <div className="add">
          <div className="right-section">
         { !isForTelebras && !isForPetrobras && !isForNilesat &&(
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/problem-add")}
            >
              + Add
            </Button>
)}
            <Button variant="primary" size="md" onClick={handleExportCSV}>
              Export to CSV
            </Button>
            <Button variant="primary" size="md" onClick={loadMyTodo}>
              My To-Do List
            </Button>

            <Button variant="primary" size="md" onClick={loadMyGroupTodo}>
              My Group To-Do List
            </Button>
            <Button variant="primary" size="md" onClick={handleReset}>
              Reset
            </Button>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid gray",
                width: "400px",
              }}
            />
          </div>
        </div>
      </div>

      <div className="Grid" style={{ height: "82%" }}>
        <Grid<ProblemData>
          rowData={filteredData}
          columnDefs={columnDefs}
          onRowClick={handleRowClick}
          onGridReady={(params: { api: GridApi<any> | null }) => {
            gridApiRef.current = params.api;
          }}
        />
      </div>

      <ProblemModal
        isOpen={isModalOpen}
        data={selectedproblem}
        onClose={closeModal}
      />
    </>
  );
}

export default ProblemView;
